import { Polygon } from "geojson";
import { Obstacle } from "../../../types";

export type Point = { x: number; y: number };

/**
 * Check if coordinates form an axis-aligned rectangle.
 * Rectangles have exactly 5 points (4 corners + closing point)
 * and all edges are either horizontal or vertical.
 */
const isAxisAlignedRectangle = (coords: number[][]): boolean => {
  if (coords.length !== 5) return false;

  // Get unique x and y values
  const xValues = new Set(coords.map(([x]) => x));
  const yValues = new Set(coords.map(([, y]) => y));

  // Axis-aligned rectangle has exactly 2 unique x values and 2 unique y values
  return xValues.size === 2 && yValues.size === 2;
};

/**
 * Determine if an obstacle is a custom polygon (not a simple rectangle).
 * Returns true for polygons with more than 4 corners or non-axis-aligned shapes.
 */
export const isPolygonObstacle = (obstacle: Obstacle): boolean => {
  const coords = obstacle.geom?.coordinates?.[0];
  if (!coords) return false;

  // Not a rectangle if more than 5 points (4 corners + closing)
  if (coords.length > 5) return true;

  // Check if it's an axis-aligned rectangle
  return !isAxisAlignedRectangle(coords);
};

/**
 * Convert an array of points to a GeoJSON Polygon geometry.
 * Automatically closes the polygon by adding the first point at the end.
 */
export const getPolygonGeometry = (points: Point[]): Polygon => ({
  type: "Polygon",
  coordinates: [
    [...points.map((p) => [p.x, p.y]), [points[0].x, points[0].y]],
  ],
});

/**
 * Calculate the bounding box of a polygon.
 */
export const getPolygonBounds = (
  points: Point[]
): { x: number; y: number; width: number; depth: number } => {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    depth: maxY - minY,
  };
};

/**
 * Get the centroid (center point) of a polygon.
 */
export const getPolygonCentroid = (points: Point[]): Point => {
  const n = points.length;
  const sumX = points.reduce((acc, p) => acc + p.x, 0);
  const sumY = points.reduce((acc, p) => acc + p.y, 0);
  return { x: sumX / n, y: sumY / n };
};

/**
 * Check if a point is inside a polygon using ray casting algorithm.
 */
export const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
  let inside = false;
  const n = polygon.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    if (
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }

  return inside;
};

/**
 * Find the closest point on a line segment to a given point.
 */
const closestPointOnSegment = (
  point: Point,
  segStart: Point,
  segEnd: Point
): { point: Point; distance: number; t: number } => {
  const dx = segEnd.x - segStart.x;
  const dy = segEnd.y - segStart.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    const dist = Math.hypot(point.x - segStart.x, point.y - segStart.y);
    return { point: segStart, distance: dist, t: 0 };
  }

  // Parameter t represents position along segment (0 = start, 1 = end)
  let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const closestX = segStart.x + t * dx;
  const closestY = segStart.y + t * dy;
  const dist = Math.hypot(point.x - closestX, point.y - closestY);

  return { point: { x: closestX, y: closestY }, distance: dist, t };
};

/**
 * Find the closest edge of a polygon to a point.
 * Returns the edge index, closest point on edge, and distance.
 */
export const findClosestEdge = (
  point: Point,
  polygon: Point[]
): { edgeIndex: number; closestPoint: Point; distance: number; t: number } | null => {
  if (polygon.length < 2) return null;

  let minDistance = Infinity;
  let result: { edgeIndex: number; closestPoint: Point; distance: number; t: number } | null = null;

  for (let i = 0; i < polygon.length; i++) {
    const start = polygon[i];
    const end = polygon[(i + 1) % polygon.length];
    const { point: closest, distance, t } = closestPointOnSegment(point, start, end);

    if (distance < minDistance) {
      minDistance = distance;
      result = { edgeIndex: i, closestPoint: closest, distance, t };
    }
  }

  return result;
};

/**
 * Translate all points in a polygon by a delta.
 */
export const translatePolygon = (
  points: Point[],
  dx: number,
  dy: number
): Point[] => {
  return points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
};

/**
 * Extract points from a GeoJSON Polygon geometry.
 * Removes the closing point (which duplicates the first).
 */
export const extractPointsFromGeometry = (geom: Polygon): Point[] => {
  const coords = geom.coordinates[0];
  // Remove the closing point (last point = first point)
  return coords.slice(0, -1).map(([x, y]) => ({ x, y }));
};

/**
 * Generate a circle as a GeoJSON Polygon with evenly-spaced points.
 * @param center - Center point of the circle
 * @param radius - Radius of the circle
 * @param numPoints - Number of points to use (default 32 for smooth appearance)
 */
export const getCircleGeometry = (
  center: Point,
  radius: number,
  numPoints = 32
): Polygon => {
  const points: [number, number][] = [];

  for (let i = 0; i < numPoints; i++) {
    const angle = (2 * Math.PI * i) / numPoints;
    const x = center.x + radius * Math.cos(angle);
    const y = center.y + radius * Math.sin(angle);
    points.push([x, y]);
  }

  // Close the polygon by repeating the first point
  points.push(points[0]);

  return {
    type: "Polygon",
    coordinates: [points],
  };
};

/**
 * Circle detection result with optional center and radius.
 */
export type CircleDetectionResult = {
  isCircle: boolean;
  center?: Point;
  radius?: number;
};

/**
 * Detect if an obstacle is a circle by checking if all points are
 * approximately equidistant from the centroid.
 *
 * Circles are stored as many-point polygons (typically 32 points).
 * We detect them by:
 * 1. Must have more than 8 points (to distinguish from octagons, etc.)
 * 2. All points must be within 1% tolerance of average distance from center
 */
export const isCircleObstacle = (obstacle: Obstacle): CircleDetectionResult => {
  const coords = obstacle.geom?.coordinates?.[0];
  if (!coords) return { isCircle: false };

  // Need many points to be a circle (more than 8)
  // Rectangles have 5, hexagons have 7, octagons have 9
  if (coords.length <= 9) return { isCircle: false };

  // Extract points (excluding closing point)
  const points = coords.slice(0, -1).map(([x, y]) => ({ x, y }));

  // Calculate centroid
  const center = getPolygonCentroid(points);

  // Calculate distances from center to each point
  const distances = points.map((p) =>
    Math.hypot(p.x - center.x, p.y - center.y)
  );

  // Calculate average distance (radius)
  const avgRadius = distances.reduce((sum, d) => sum + d, 0) / distances.length;

  // Check if all distances are within 1% tolerance of average
  const tolerance = avgRadius * 0.01;
  const isCircle = distances.every(
    (d) => Math.abs(d - avgRadius) <= tolerance
  );

  if (isCircle) {
    return { isCircle: true, center, radius: avgRadius };
  }

  return { isCircle: false };
};
